import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import {
  Col,
  Row,
  Panel,
  FormGroup,
  Button,
} from 'react-bootstrap';
import { Palette, Check, Save, Loader2, Heart, Zap, Star, Users, Ruler, AlertTriangle, CheckCircle } from 'lucide-react';

import s from './CardStyleSettings.css';

// Card style options with previews
const CARD_STYLES = [
  {
    id: 'classic',
    name: 'Clásico (Original)',
    nameEn: 'Classic (Original)',
    description: 'Diseño original del sitio. Sin cambios, mantiene el look actual.',
    descriptionEn: 'Original site design. No changes, keeps current look.',
  },
  {
    id: 'standard',
    name: 'Estándar Modernizado',
    nameEn: 'Modern Standard',
    description: 'Imagen arriba (60%), contenido blanco abajo. Bordes redondeados y sombras suaves.',
    descriptionEn: 'Image on top (60%), white content below. Rounded corners and soft shadows.',
  },
  {
    id: 'transparent',
    name: 'Transparencia Elegante',
    nameEn: 'Elegant Transparency',
    description: 'Imagen de fondo completo con degradado blanco sutil. Texto oscuro flotante.',
    descriptionEn: 'Full background image with subtle white gradient. Dark floating text.',
  },
  {
    id: 'dark-grad',
    name: 'Degradado Oscuro',
    nameEn: 'Dark Gradient',
    description: 'Imagen de fondo con degradado oscuro fuerte. Texto blanco para máximo contraste.',
    descriptionEn: 'Background image with strong dark gradient. White text for maximum contrast.',
  },
  {
    id: 'light-grad',
    name: 'Degradado Claro',
    nameEn: 'Light Gradient',
    description: 'Imagen de fondo con degradado blanco. Texto oscuro sobre fondo claro.',
    descriptionEn: 'Background image with white gradient. Dark text on light background.',
  },
];

class CardStyleSettings extends React.Component {
  static propTypes = {
    cardStyleVariant: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedStyle: props.cardStyleVariant || 'standard',
      saving: false,
      successMessage: null,
      errorMessage: null,
    };
  }

  componentDidMount() {
    this.loadCurrentStyle();
  }

  loadCurrentStyle = async () => {
    try {
      const response = await fetch('/web-api/site-settings/cardStyleVariant', {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success && data.value) {
        this.setState({ selectedStyle: data.value });
      }
    } catch (error) {
      console.error('Error loading card style:', error);
    }
  };

  handleStyleSelect = (styleId) => {
    this.setState({ selectedStyle: styleId });
  };

  handleSave = async () => {
    const { selectedStyle } = this.state;
    
    this.setState({ saving: true, successMessage: null, errorMessage: null });

    try {
      const response = await fetch('/web-api/admin/update-card-style', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ cardStyleVariant: selectedStyle }),
      });

      const data = await response.json();

      if (data.success) {
        this.setState({
          saving: false,
          successMessage: '✅ Estilo guardado. Recarga la página de inicio (Ctrl+F5) para ver los cambios.',
        });
        setTimeout(() => this.setState({ successMessage: null }), 8000);
      } else {
        throw new Error(data.error || 'Error al guardar');
      }
    } catch (error) {
      this.setState({
        saving: false,
        errorMessage: `❌ Error: ${error.message}`,
      });
      setTimeout(() => this.setState({ errorMessage: null }), 5000);
    }
  };

  render() {
    const { selectedStyle, saving, successMessage, errorMessage } = this.state;

    return (
      <div className={s.container}>
        <div className={s.header}>
          <h1 className={s.title}><Palette size={28} className={s.titleIcon} /> Apariencia de Tarjetas</h1>
          <p className={s.subtitle}>
            Selecciona el estilo visual de las tarjetas de barcos en la página principal
          </p>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className={s.alertSuccess}><CheckCircle size={18} style={{marginRight: 8}} />{successMessage}</div>
        )}
        {errorMessage && (
          <div className={s.alertError}><AlertTriangle size={18} style={{marginRight: 8}} />{errorMessage}</div>
        )}

        {/* Style Options Grid */}
        <div className={s.stylesGrid}>
          {CARD_STYLES.map((style) => (
            <div
              key={style.id}
              className={cx(s.styleOption, {
                [s.styleOptionSelected]: selectedStyle === style.id,
              })}
              onClick={() => this.handleStyleSelect(style.id)}
            >
              {/* Preview - Realistic Card */}
              <div className={cx(s.stylePreview, s[`preview${style.id.charAt(0).toUpperCase() + style.id.slice(1).replace(/-/g, '')}`])}>
                {/* Background Image */}
                <div className={s.previewImage}>
                  <img src="https://images.unsplash.com/photo-1605281317010-fe5ffe798166?w=500&h=400&fit=crop" alt="Yacht" />
                </div>
                
                {/* Wishlist Heart */}
                <div className={s.previewHeart}><Heart size={16} /></div>
                
                {/* Instant Booking Badge */}
                <div className={s.previewInstant}><Zap size={12} /> Reserva Instantánea</div>
                
                {/* Content Overlay */}
                <div className={s.previewOverlay}>
                  <div className={s.previewContent}>
                    {/* Category & Rating Row */}
                    <div className={s.previewTopRow}>
                      <span className={s.previewCategory}>Yate de Lujo</span>
                      <span className={s.previewRating}>
                        <Star size={12} className={s.previewStar} /> 4.9
                      </span>
                    </div>
                    
                    {/* Title */}
                    <span className={s.previewTitle}>Azimut 55 Flybridge</span>
                    
                    {/* Specs */}
                    <div className={s.previewSpecs}>
                      <span className={s.previewSpec}><Users size={12} /> 8 personas</span>
                      <span className={s.previewSpec}><Ruler size={12} /> 16m</span>
                    </div>
                    
                    {/* Price & Button Row */}
                    <div className={s.previewPriceRow}>
                      <span className={s.previewPrice}>
                        $1,200 <span className={s.previewPriceUnit}>/día</span>
                      </span>
                      <div className={s.previewButton}>Reserva ya →</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className={s.styleInfo}>
                <div className={s.styleHeader}>
                  <h3 className={s.styleName}>{style.name}</h3>
                  {selectedStyle === style.id && (
                    <span className={s.selectedBadge}><Check size={14} /> Seleccionado</span>
                  )}
                </div>
                <p className={s.styleDescription}>{style.description}</p>
              </div>

              {/* Radio indicator */}
              <div className={s.radioIndicator}>
                <div className={cx(s.radioOuter, { [s.radioSelected]: selectedStyle === style.id })}>
                  {selectedStyle === style.id && <div className={s.radioInner}></div>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Save Button */}
        <div className={s.saveSection}>
          <Button
            bsStyle="primary"
            bsSize="large"
            className={s.saveButton}
            onClick={this.handleSave}
            disabled={saving}
          >
            {saving ? <><Loader2 size={18} className={s.spinning} /> Guardando...</> : <><Save size={18} /> Guardar Cambios</>}
          </Button>
        </div>
      </div>
    );
  }
}

const mapState = (state) => ({
  cardStyleVariant: state?.siteSettings?.data?.cardStyleVariant,
});

export default injectIntl(withStyles(s)(connect(mapState)(CardStyleSettings)));
